import { internalClientIds, JobType } from 'shared/common/job-constants';
import { InterviewKind } from 'shared/models/interview';
import { JobStatus } from 'shared/models/job';
import {
    awaitingClientStage,
    clientFinalRoundStage,
    clientFirstRoundStage,
    clientMiddleRoundStage,
    offerStage,
    rocketScreenCompleteStage,
    rocketScreenScheduledStage
} from 'shared/models/job-stages';

import { Duration } from './duration';

export type PipelineView = 'current' | 'lost';
export type PipelineStage =
    | 'phoneCalls'
    | 'phoneScreensComplete'
    | 'awaitingFeedback'
    | 'firstRounds'
    | 'secondRounds'
    | 'finalRounds'
    | 'offers';

interface PipelineStageDefinition {
    id: PipelineStage;
    stage: string;
    interviewKind?: InterviewKind;
}

export const pipelineStages: PipelineStageDefinition[] = [
    {
        id: 'phoneCalls',
        interviewKind: 'Rocket Screen',
        stage: rocketScreenScheduledStage
    },
    { id: 'phoneScreensComplete', interviewKind: 'Rocket Screen', stage: rocketScreenCompleteStage },
    { id: 'awaitingFeedback', stage: awaitingClientStage },
    { id: 'firstRounds', interviewKind: 'Client First Round', stage: clientFirstRoundStage },
    { id: 'secondRounds', interviewKind: 'Client Middle Round', stage: clientMiddleRoundStage },
    { id: 'finalRounds', interviewKind: 'Client Final Round', stage: clientFinalRoundStage },
    { id: 'offers', stage: offerStage }
];

export const getPipelineStages = (view: PipelineView) =>
    pipelineStages.filter((stage) =>
        view === 'current' ? stage.id !== 'phoneScreensComplete' : stage.id !== 'phoneCalls'
    );

interface StringFilter {
    _eq?: string;
    _neq?: string;
    _in?: string[];
    _nin?: string[];
}

export interface PipelineWhere {
    _and?: PipelineWhere[];
    _or?: PipelineWhere[];
    personId?: StringFilter;
    jobId?: StringFilter;
    assignee?: StringFilter;
    stage?: StringFilter;
    disqualified?: { _eq: boolean };
    disqualifiedAt?: { _gte: number; _lte: number };
    interviews?: {
        kind: { _eq: InterviewKind };
        startTime: { _gt: number };
    };
    job?: {
        clientId?: StringFilter;
        jobType?: StringFilter;
        status?: { _in: JobStatus[] };
    };
}

export interface PipelineCandidateKey {
    personId: string;
    jobId: string;
}

export interface PipelineCount {
    aggregate: { count: number };
}

export interface RecruiterPipelineRow {
    id: string;
    name: string;
    username: string;
    asOf: number;
    phoneCalls: number;
    phoneScreensComplete: number;
    awaitingFeedback: number;
    firstRounds: number;
    secondRounds: number;
    finalRounds: number;
    offers: number;
}

export const getPipelineWhere = (
    view: PipelineView,
    jobTypes: JobType[],
    duration: Pick<Duration, 'start' | 'end'>,
    now = Date.now()
): PipelineWhere => ({
    ...(view === 'current'
        ? {
              _or: [
                  { stage: { _neq: rocketScreenScheduledStage } },
                  { interviews: { kind: { _eq: 'Rocket Screen' as InterviewKind }, startTime: { _gt: now } } }
              ]
          }
        : {}),
    disqualified: { _eq: view === 'lost' },
    ...(view === 'lost' ? { disqualifiedAt: { _gte: duration.start, _lte: duration.end } } : {}),
    job: {
        clientId: { _nin: internalClientIds },
        jobType: { _in: jobTypes },
        status: { _in: [JobStatus.Active] }
    },
    stage: { _in: getPipelineStages(view).map(({ stage }) => stage) }
});

export const getCandidateKeysWhere = (candidates: PipelineCandidateKey[]): PipelineWhere => ({
    _or: candidates.map(({ personId, jobId }) => ({ personId: { _eq: personId }, jobId: { _eq: jobId } }))
});
